import React from "react";
import { FiCpu, FiZap, FiTrendingUp, FiShield } from "react-icons/fi";
import { HealthRing, MiniBar, riskColor } from "./stpHelpers";

const PRIORITY = {
  High:   { color: "#ef4444", bg: "#fee2e2" },
  Medium: { color: "#f59e0b", bg: "#fef3c7" },
  Low:    { color: "#3b82f6", bg: "#dbeafe" },
};

const StpAiPanel = ({ d }) => {
  const { aiInsights, summary, pumps, aeration } = d;

  const highRiskPumps  = pumps.filter(p => p.failureRisk >= 30);
  const energyKw       = aeration.blowers.reduce((s, b) => s + b.power, 0);
  const energySavingPct = 8;

  return (
    <div className="stp-ai-panel">

      {/* Score cards */}
      <div className="stp-ai-scores">
        <div className="stp-ai-score-card">
          <HealthRing score={summary.healthScore} size={90} />
          <div>
            <div className="stp-ai-sc-title">Efficiency Score</div>
            <div className="stp-ai-sc-sub">Based on DO stability, flow balance, sludge ratio and energy consumption</div>
          </div>
        </div>

        <div className="stp-ai-score-card">
          <HealthRing score={d.compliance.status === "Compliant" ? 95 : 40} size={90} />
          <div>
            <div className="stp-ai-sc-title">Compliance Score</div>
            <div className="stp-ai-sc-sub">All outlet parameters within MPCB discharge limits for 29 of last 30 days</div>
          </div>
        </div>

        <div className="stp-ai-savings-card">
          <div className="stp-ai-sav-val">{(energyKw * energySavingPct / 100).toFixed(1)} kW</div>
          <div className="stp-ai-sav-lbl">Potential Energy Saving</div>
          <div className="stp-ai-sav-sub">By optimizing blower schedule (est. {energySavingPct}% reduction)</div>
          <div className="stp-ai-sav-money">≈ ₹{Math.round(energyKw * 8 * energySavingPct / 100 * 24)}/day saved</div>
        </div>
      </div>

      {/* 4 AI modules */}
      <div className="stp-ai-modules">
        {/* Efficiency */}
        <div className="stp-aim-card" style={{ borderTopColor: "#667eea" }}>
          <div className="stp-aim-head"><FiTrendingUp size={16} color="#667eea" /> Efficiency Analysis</div>
          <div className="stp-aim-items">
            {[
              { label: "DO Stability",        score: 58, col: "#f59e0b" },
              { label: "Flow Balance",         score: 82, col: "#10b981" },
              { label: "Sludge Ratio",         score: 78, col: "#10b981" },
              { label: "Energy Consumption",   score: 74, col: "#f59e0b" },
            ].map(item => (
              <div key={item.label} className="stp-aim-row">
                <span>{item.label}</span>
                <MiniBar value={item.score} max={100} color={item.col} height={6} />
                <span style={{ color: item.col, minWidth: 36, textAlign: "right" }}>{item.score}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Predictive Maintenance */}
        <div className="stp-aim-card" style={{ borderTopColor: "#ef4444" }}>
          <div className="stp-aim-head"><FiCpu size={16} color="#ef4444" /> Predictive Maintenance</div>
          <div className="stp-aim-items">
            {pumps.map(p => (
              <div key={p.id} className="stp-aim-row">
                <span style={{ fontSize: 11 }}>{p.name.split(" ").slice(0,3).join(" ")}</span>
                <MiniBar value={p.failureRisk} max={100} color={riskColor(p.failureRisk)} height={6} />
                <span style={{ color: riskColor(p.failureRisk), minWidth: 36, textAlign: "right" }}>{p.failureRisk}%</span>
              </div>
            ))}
            <div style={{ marginTop: 8 }}>
              {aeration.blowers.filter(b => b.status !== "Running").length > 0 && (
                <div className="stp-aim-warn">⚠ BL-04 offline — predict diffuser clogging if not resolved in 24h</div>
              )}
            </div>
          </div>
        </div>

        {/* Energy Optimization */}
        <div className="stp-aim-card" style={{ borderTopColor: "#f59e0b" }}>
          <div className="stp-aim-head"><FiZap size={16} color="#f59e0b" /> Energy Optimization</div>
          <div className="stp-aim-suggestions">
            <div className="stp-aim-sug">💡 Reduce BL-03 RPM by 10% (1380→1240) during 10:00–14:00 off-peak — saves ~1.8 kW</div>
            <div className="stp-aim-sug">💡 Shift sludge pump cycle to 02:00–04:00 (off-peak tariff) — saves ~₹180/day</div>
            <div className="stp-aim-sug">💡 Aeration cycle optimization: run 45 min ON / 15 min OFF during low-load night period</div>
            <div className="stp-aim-total">Estimated total saving: ₹{Math.round(energyKw * 8 * 0.12 * 24)}/month</div>
          </div>
        </div>

        {/* Compliance Risk */}
        <div className="stp-aim-card" style={{ borderTopColor: "#10b981" }}>
          <div className="stp-aim-head"><FiShield size={16} color="#10b981" /> Compliance Risk Prediction</div>
          <div className="stp-aim-items">
            {[
              { label: "pH (outlet)",   risk: 22, trend: "↑ Trending up",    col: "#f59e0b" },
              { label: "BOD",           risk: 5,  trend: "→ Stable",         col: "#10b981" },
              { label: "COD",           risk: 8,  trend: "→ Stable",         col: "#10b981" },
              { label: "TSS",           risk: 12, trend: "→ Stable",         col: "#10b981" },
              { label: "Turbidity",     risk: 7,  trend: "→ Stable",         col: "#10b981" },
            ].map(item => (
              <div key={item.label} className="stp-aim-row">
                <span>{item.label}</span>
                <span className="stp-aim-trend" style={{ color: item.col }}>{item.trend}</span>
                <span style={{ color: item.col, fontSize: 11, minWidth: 50, textAlign: "right" }}>Risk: {item.risk}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Insight cards */}
      <div className="stp-section-title" style={{ marginTop: 20 }}>Active AI Recommendations</div>
      <div className="stp-insight-list">
        {aiInsights.map(ins => {
          const pm = PRIORITY[ins.priority];
          return (
            <div key={ins.id} className="stp-insight-card" style={{ borderLeftColor: pm.color }}>
              <div className="stp-ins-top">
                <span className="stp-ins-icon">{ins.icon}</span>
                <div>
                  <div className="stp-ins-title">{ins.title}</div>
                  <span className="stp-badge" style={{ background: pm.bg, color: pm.color, fontSize: 11 }}>{ins.priority} Priority</span>
                </div>
              </div>
              <div className="stp-ins-text">{ins.insight}</div>
              <div className="stp-ins-footer">
                <span style={{ fontSize: 11, color: "#9ca3af" }}>{ins.id}</span>
                <button className="stp-ctrl-btn stp-ctrl-purple" style={{ padding: "4px 12px", fontSize: 12 }}>{ins.action}</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StpAiPanel;
